import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

export const CLAIM_INSURER_CATEGORY_FILTERS = [
  { key: 'ALL', label: '전체' },
  { key: 'LIFE', label: '생명보험' },
  { key: 'NONLIFE', label: '손해보험' },
  { key: 'PET', label: '펫보험' },
  { key: 'LIABILITY', label: '배상책임' },
]

function findInsurerRootId(orgOptions, matcher) {
  const root = (orgOptions || []).find((o) =>
    o?.type === 'INSURER'
    && !o?.parentOrgId
    && matcher(String(o?.name || '')),
  )
  return root?.id != null ? Number(root.id) : null
}

function isChildOfRoot(org, rootId) {
  return rootId != null && Number(org?.parentOrgId) === rootId
}

/**
 * 운영자 청구 관련 화면용: /orgs/options(scope=all)에서 청구 카테고리 하위 보험사(회사) 목록.
 */
export function useClaimAdminInsurers() {
  const [orgOptions, setOrgOptions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await api.get('/orgs/options',
          { params: { scope: 'all', includeClaimInsurerCategories: true } },
        )
        const payload = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : [])
        if (!cancelled) setOrgOptions(Array.isArray(payload) ? payload : [])
      } catch {
        if (!cancelled) setOrgOptions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const lifeRootId = useMemo(() => findInsurerRootId(orgOptions, (name) => name.includes('생명')), [orgOptions])

  const nonlifeRootId = useMemo(() => {
    return findInsurerRootId(orgOptions, (name) =>
      name.includes('손해') || name.includes('화재') || name.includes('해상'))
  }, [orgOptions])

  const petRootId = useMemo(() => findInsurerRootId(orgOptions, (name) => name.includes('펫')), [orgOptions])

  const liabilityRootId = useMemo(() => findInsurerRootId(orgOptions, (name) => name.includes('배상')), [orgOptions])

  const allInsurers = useMemo(() => (orgOptions || [])
    .filter((o) => o?.type === 'INSURER' && o?.parentOrgId && (
      isChildOfRoot(o, lifeRootId)
      || isChildOfRoot(o, nonlifeRootId)
      || isChildOfRoot(o, petRootId)
      || isChildOfRoot(o, liabilityRootId)
    ))
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko')),
  [orgOptions, lifeRootId, nonlifeRootId, petRootId, liabilityRootId])

  return { allInsurers, lifeRootId, nonlifeRootId, petRootId, liabilityRootId, loading, orgOptions }
}

export function claimInsurerCategoryKey(org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId) {
  if (isChildOfRoot(org, lifeRootId)) return 'LIFE'
  if (isChildOfRoot(org, nonlifeRootId)) return 'NONLIFE'
  if (isChildOfRoot(org, petRootId)) return 'PET'
  if (isChildOfRoot(org, liabilityRootId)) return 'LIABILITY'
  return null
}

export function claimInsurerCategoryLabel(org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId) {
  const k = claimInsurerCategoryKey(org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId)
  if (k === 'LIFE') return '생명'
  if (k === 'NONLIFE') return '손해'
  if (k === 'PET') return '펫'
  if (k === 'LIABILITY') return '배상'
  return '-'
}

export function matchesClaimInsurerCategory(org, categoryKey, lifeRootId, nonlifeRootId, petRootId, liabilityRootId) {
  if (categoryKey === 'ALL') return true
  return claimInsurerCategoryKey(org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId) === categoryKey
}
